import {
  Box,
  Stack,
  Text,
  Heading,
  Button,
  IconArrow,
  Columns,
  Column,
  Bleed,
  Page,
  PageBlock,
} from 'braid-design-system';
import { useRef } from 'react';
import { Helmet } from 'react-helmet';

import {
  type FAQ,
  FaqAccordion,
} from 'src/components/FaqAccordion/FaqAccordion';
import { SiteWideFooter } from 'src/components/SiteWideFooter';

import { AdSelection } from './components/AdSelection';
import { ExclusiveResources } from './components/ExclusiveResources';
import { Testimonial } from './components/Testimonial';

import * as styles from './PrivateSellerSEM.css';

const slideTexts = [
  'Restaurant',
  'Beauty Spa',
  'Gym Studio',
  'Online Store',
  'Post Office',
  'Pub & Café',
  'Bottle Shop',
  'Supermarket',
  'Hair Salon',
  'Laundromat',
  'Restaurant',
];

const faqs: FAQ[] = [
  {
    question: 'How does the process work?',
    answer:
      "Sign up, choose an ad pack, and create and publish your ad. You'll get access to resources and once your ad is live we'll match you with buyers who will contact you directly.",
  },
  {
    question: 'Who should advertise on SEEK Business?',
    answer:
      'Anybody who owns a business and would like to sell it privately by liaising directly with buyers.',
  },
  {
    question: 'How long will it take me to sell my business?',
    answer:
      'On average it takes 6-9 months to sell, sometimes more especially if your business is valued at $1M or more.',
  },
  {
    question: 'Do you charge commission?',
    answer:
      'No, we do not charge commission and there are no hidden fees. We charge a flat rate to advertise.',
  },
  {
    question: 'Will my ad be placed on other websites?',
    answer:
      'No, when you advertise with SEEK Business your ad is only displayed on our website.',
  },
  {
    question:
      "I'm not sure how to go about listing my business, where can I start?",
    answer:
      "Sign up with SEEK Business to access our helpful resources. Then purchase an ad pack if and when you're ready.",
  },
  {
    question: 'How do I determine the value of my business?',
    answer:
      'Sign up with SEEK Business to access our value calculator guide to get an idea of where to start.',
  },
];

const PrivateSellerSEM = () => {
  const scrollTarget = useRef<HTMLElement | null>(null);
  const scrollToPackage = () =>
    scrollTarget.current?.scrollIntoView({ behavior: 'smooth' });

  return (
    <Page footer={<SiteWideFooter />}>
      <Helmet>
        <title>SEEK Business - Sell Your Business</title>
        <meta name="robots" content="noindex" />
        <meta name="robots" content="nofollow" />
      </Helmet>

      <Stack space={{ mobile: 'xxlarge', tablet: 'xxxlarge' }}>
        <Box
          id="jumbotron-white-background"
          padding={{
            mobile: 'xlarge',
            tablet: 'xxlarge',
          }}
          background="brandAccent"
        >
          <PageBlock>
            <Box id="jumbotron-content" className={styles.title.main}>
              <Stack space="xlarge" align="left">
                {/* Intro Text */}
                <Bleed horizontal="xlarge">
                  <Box className={styles.title.mainContainer}>
                    <Heading level="1">
                      <Columns space="xxsmall">
                        <Column>
                          <div className={styles.title.column.left}>
                            Sell&nbsp;your&nbsp;
                          </div>
                        </Column>
                        <Column>
                          <div className={styles.title.column.right}>
                            <ul className={styles.slide.container}>
                              {slideTexts.map((text, index) => (
                                <li
                                  className={styles.slide.text}
                                  key={`slide-${index}`}
                                >
                                  {text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </Column>
                      </Columns>
                    </Heading>
                  </Box>
                </Bleed>
                <Text>
                  Whether you’re ready to sell now or in the future, SEEK
                  Business has everything you need to sell your business with
                  confidence.
                </Text>
                <Box className={styles.choosePackageButton}>
                  <Button
                    tone="neutral"
                    variant="ghost"
                    onClick={scrollToPackage}
                  >
                    Choose Package&nbsp;&nbsp;
                    <IconArrow direction="down" />
                  </Button>
                </Box>
              </Stack>
            </Box>
          </PageBlock>
        </Box>

        <PageBlock width="medium">
          <Stack space={{ mobile: 'xxlarge', tablet: 'xxxlarge' }}>
            <AdSelection containerRef={scrollTarget} />
            <FaqAccordion faqs={faqs} />
            <Testimonial />
            <ExclusiveResources />
          </Stack>
        </PageBlock>
      </Stack>
    </Page>
  );
};

export default PrivateSellerSEM;
